/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
export type UseFormFieldValidationProps = {
  validate: () => boolean;
  hasFormError?: boolean;
  formErrorMessage?: string;
  fieldErrorMessage?: string;
};

/**
 * The useFormFieldValidation contains logic that allows the consumer to quickly
 * add field validation to a form.
 * 
 * It accounts for a potential 'parent' form validation error, but gives the field
 * validation error precedence.
 * 
 * You can expect this to return if there's an overall error (field or form) along
 * with it's respective error message if there is one.
 * 
 * ```typescript
 * 
 * const [hasFieldValidationError, errorMessage] = useFormFieldValidation({
 *   validate          : () => isPositiveInteger(value),
 *   hasFormError      : formHasError,
 *   formErrorMessage  : formErrorText,
 *   fieldErrorMessage : translate('common:validation.errorMessages.positiveInteger')
 * });
 * 
 * ```
 */
export const useFormFieldValidation = ({
  validate,
  formErrorMessage = '',
  fieldErrorMessage = '',
  hasFormError = false
}: UseFormFieldValidationProps): [boolean, string] => {
  const hasFieldValidationError = !validate();
  const hasError = hasFieldValidationError || hasFormError;
  const getErrorMessage = (): string => {
    if (hasFieldValidationError) {
      return fieldErrorMessage;
    } else if (hasFormError) {
      return formErrorMessage;
    }
    return '';
  };
  return [hasError, getErrorMessage()];
};

export default useFormFieldValidation;
